import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { api, formatErr } from "../../api";
import { Loader2, Sparkles } from "lucide-react";

const DESSERT_COLOURS = [
  "#6B7B4A", "#A3B18A", "#DDA15E", "#BC6C25", "#8FA469",
  "#C9A66B", "#7B8B5C", "#E6C88C", "#5D6C42", "#B48A5C",
];

/**
 * Generic category-sales breakdown. Fetches
 *   GET /api/mgmt/reports/category-breakdown?month=YYYY-MM&category=<category>
 * which aggregates `sales.items[*].category` across all POS imports and manual
 * sales for the month. Renders KPI ribbon + pie chart + flavour table.
 *
 * Optional `reclassify` block wires the dessert-only bulk-move-into-Desserts
 * flow. Pastry / Ice-cream / etc. skip it (they use the keyword classifier
 * on POS commit, no strict allow-list).
 *
 * The endpoint hits whatever backend REACT_APP_BACKEND_URL points at, so
 * in production this reads live POS data with zero code changes.
 */
export function CategoryBreakdown({
  category = "Desserts",
  label,
  headline,
  subhead,
  emptyBody,
  emptyHint,
  testidPrefix = "sales-category",
  colours = DESSERT_COLOURS,
  showReclassify = false,
}) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [reclassResult, setReclassResult] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(
        `/mgmt/reports/category-breakdown?month=${month}&category=${encodeURIComponent(category)}`
      );
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, category]);

  // Poll every 30 s + refresh whenever the tab regains focus so any product
  // rename or reclassify on another tab (or the Catalog page) shows up here
  // without a full page reload. The endpoint is cheap; this is the fastest
  // way to guarantee "changed durian to burned cheesecake" reflects.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const t = setInterval(load, 30000);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(t);
    };
  // eslint-disable-next-line
  }, [month, category]);

  // Scan every product name for keywords and bulk-move into `category`.
  // Only exposed for Desserts today (strict allow-list) — Pastry uses the
  // per-item keyword classifier on POS commit so no bulk fix-up is needed.
  const runReclassify = async (dryRun = false, productIds = null) => {
    setErr("");
    setReclassifying(true);
    try {
      const url = `/mgmt/products/reclassify?category=${encodeURIComponent(category)}${dryRun ? "&dry_run=true" : ""}`;
      const payload = dryRun ? {} : (productIds !== null ? { product_ids: productIds } : {});
      const { data: res } = await api.post(url, payload);
      setReclassResult(res);
      if (!dryRun) await load();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setReclassifying(false);
    }
  };

  const items = data?.items || [];
  const total = data?.total_revenue || 0;
  const totalQty = data?.total_qty || 0;

  return (
    <div className="space-y-6" data-testid={`${testidPrefix}-root`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-matcha-dark">{label || category}</p>
          <h2 className="font-display text-2xl text-ink" style={{ letterSpacing: "0.02em" }}>
            {headline || `${label || category} · by flavour`}
          </h2>
          <p className="text-ink-soft text-sm mt-1 max-w-xl">
            {subhead || (
              <>Auto-tabulated from your POS imports — any sale item tagged
                <strong className="text-ink"> Category = &quot;{category}&quot;</strong> shows up here.</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {showReclassify && (
            <button
              type="button"
              data-testid={`${testidPrefix}-reclassify`}
              disabled={reclassifying}
              onClick={() => runReclassify(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-matcha-dark/40 text-matcha-dark hover:bg-sage/20 transition-colors text-sm disabled:opacity-50"
              title={`Scan every product name for ${category.toLowerCase()} keywords and preview which products would move into ${category}.`}
            >
              {reclassifying ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
              Find &amp; group {category.toLowerCase()}
            </button>
          )}
          <label className="text-xs text-ink-soft uppercase tracking-[0.16em]">Month</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-cream border border-ink/15 rounded-sm px-3 py-2 focus:outline-none focus:border-matcha-dark text-ink text-sm"
            data-testid={`${testidPrefix}-month`}
          />
        </div>
      </div>

      {err && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-sm" data-testid={`${testidPrefix}-err`}>
          {err}
        </p>
      )}

      {showReclassify && reclassResult && (
        <ReclassifyPanel
          result={reclassResult}
          category={category}
          testidPrefix={testidPrefix}
          onApply={(ids) => runReclassify(false, ids)}
          onDismiss={() => setReclassResult(null)}
          loading={reclassifying}
        />
      )}

      {loading && (
        <p className="text-ink-soft text-sm inline-flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Loading…
        </p>
      )}

      {!loading && items.length === 0 && (
        <div className="rounded-sm border border-dashed border-ink/15 p-8 bg-cream/30 text-center" data-testid={`${testidPrefix}-empty`}>
          <p className="text-ink">No {(label || category).toLowerCase()} sales for <strong>{month}</strong>.</p>
          {emptyBody && (
            <p className="text-ink-soft text-xs mt-2 max-w-md mx-auto">{emptyBody}</p>
          )}
          {emptyHint && (
            <p className="text-ink-soft text-[11px] mt-3 italic">{emptyHint}</p>
          )}
        </div>
      )}

      {!loading && items.length > 0 && (
        <>
          {/* KPI ribbon */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label="Revenue"   value={`RM ${money(total)}`}          testid={`${testidPrefix}-total-revenue`} />
            <Kpi label="Units Sold" value={`${totalQty}`}                testid={`${testidPrefix}-total-qty`} />
            <Kpi label="Flavours"  value={`${items.length}`}             sub="Distinct SKUs" />
            <Kpi label="Top Seller" value={items[0]?.name || "—"}
                 sub={items[0] ? `RM ${money(items[0].revenue)} · ${items[0].qty} sold` : ""} />
          </div>

          {/* Family split — always renders when the query spans 2+ product
              categories (e.g. Drinks tab = Matcha Drinks + Coffee), even
              when one family has zero sales this month. Silent for
              single-category views like Desserts / Pastry. */}
          <FamilySplit
            items={items}
            total={total}
            totalQty={totalQty}
            requestedCategories={(data?.category || category).split(",").map((c) => c.trim()).filter(Boolean)}
            testidPrefix={testidPrefix}
          />

          {/* Pie + Table */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div style={{ width: "100%", height: 380 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={items}
                    dataKey="revenue"
                    nameKey="name"
                    cx="50%" cy="50%"
                    outerRadius={120} innerRadius={60}
                    paddingAngle={2}
                    stroke="#F5F1E6" strokeWidth={2}
                    label={({ percent }) => percent > 0.06 ? `${(percent * 100).toFixed(0)}%` : ""}
                  >
                    {items.map((_, i) => (
                      <Cell key={i} fill={colours[i % colours.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, _n, entry) => [
                      `RM ${Number(value).toFixed(2)} · ${entry.payload.qty} sold`,
                      entry.payload.name,
                    ]}
                    contentStyle={{ background: "#FFFDF7", border: "1px solid #E5E0D3", borderRadius: 2, fontSize: 12 }}
                  />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`${testidPrefix}-table`}>
                <thead className="text-left text-ink-soft border-b border-ink/10">
                  <tr>
                    <th className="pb-2 overline">#</th>
                    <th className="pb-2 overline">Flavour</th>
                    <th className="pb-2 overline text-right">Qty</th>
                    <th className="pb-2 overline text-right">Revenue</th>
                    <th className="pb-2 overline text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={`${it.product_id || "x"}-${i}`} className="border-t border-ink/5" data-testid={`${testidPrefix}-row-${i}`}>
                      <td className="py-2 text-ink-soft">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                          style={{ background: colours[i % colours.length] }}
                        />
                        {i + 1}
                      </td>
                      <td className="py-2 text-ink truncate max-w-xs" title={it.name}>{it.name}</td>
                      <td className="py-2 text-right text-ink whitespace-nowrap">{it.qty}</td>
                      <td className="py-2 text-right text-ink whitespace-nowrap">RM {money(it.revenue)}</td>
                      <td className="py-2 text-right text-ink-soft whitespace-nowrap">
                        {total > 0 ? `${((it.revenue / total) * 100).toFixed(1)}%` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Backwards-compat default export: the Desserts variant with the strict
// allow-list reclassifier + dessert-specific empty-state copy.
export default function DessertsBreakdown() {
  return (
    <CategoryBreakdown
      category="Desserts,Desserts - Combo"
      label="Desserts"
      headline="Cheesecake · Tiramisu · Combo · Top-up · by flavour"
      testidPrefix="sales-desserts"
      showReclassify
      emptyBody={<>
        Upload your GEBME export in <strong className="text-ink">Import from POS</strong>. Dessert
        names on the strict allow-list (cheesecake / tiramisu / combo / top-up) are auto-tagged
        <strong className="text-ink"> Category = Desserts</strong> on commit and show up here immediately.
      </>}
      emptyHint={<>
        Anything the classifier can&apos;t identify lands in <code className="text-ink">Uncategorised</code>.
        Re-upload the file and pick <strong>+ Create as Dessert</strong> on the review row to force it.
      </>}
    />
  );
}

// Family split strip — groups items by their `category` field (returned per
// row by the multi-category endpoint) and shows units + revenue + share per
// family. Only renders when 2+ distinct categories are present so it stays
// invisible for single-category views (Desserts, Pastry). Perfect for the
// Drinks tab where admins want to see Matcha vs Coffee at a glance.
function FamilySplit({ items, total, totalQty, requestedCategories = [], testidPrefix }) {
  // Legacy category aliases — after the "Matcha Drinks → Drink - Matcha"
  // rename we still query the endpoint with the old names for backward
  // compat on any un-migrated tenants. Fold them into the new label here
  // so admins see a single Matcha card, not one full + one zero-fill.
  const LEGACY = {
    "Matcha Drinks": "Drink - Matcha",
    "Coffee":        "Drink - Coffee",
  };
  const normalise = (c) => LEGACY[c] || c;

  // Group by resolved category, preserving insertion order (items[] is
  // pre-sorted by revenue desc so the map keys land in the same order).
  const map = new Map();
  // Zero-fill every REQUESTED family first so the user always sees the full
  // comparison — a Coffee card shows "0 sold · RM 0.00" instead of vanishing.
  requestedCategories.forEach((c) => {
    if (!c) return;
    const key = normalise(c);
    if (!map.has(key)) {
      map.set(key, { category: key, qty: 0, revenue: 0, flavours: 0 });
    }
  });
  items.forEach((it) => {
    const cat = normalise((it.category || "Uncategorised").trim());
    const b = map.get(cat) || { category: cat, qty: 0, revenue: 0, flavours: 0 };
    b.qty += Number(it.qty || 0);
    b.revenue += Number(it.revenue || 0);
    b.flavours += 1;
    map.set(cat, b);
  });
  const families = Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
  if (families.length < 2) return null;

  // Bright, family-specific palette so Matcha reads green + Coffee reads brown.
  const familyColour = (cat) => {
    const c = cat.toLowerCase();
    if (c.includes("matcha")) return "#6B7B4A";
    if (c.includes("coffee")) return "#8D5A2C";
    if (c.includes("dessert")) return "#DDA15E";
    if (c.includes("pastry")) return "#C9A66B";
    return "#A3B18A";
  };

  return (
    <div
      className="rounded-sm border border-ink/10 bg-white/60 p-4"
      data-testid={`${testidPrefix}-family-split`}
    >
      <p className="overline text-ink-soft mb-3">Family split</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {families.map((f) => {
          const revShare = total > 0 ? (f.revenue / total) * 100 : 0;
          const qtyShare = totalQty > 0 ? (f.qty / totalQty) * 100 : 0;
          const colour = familyColour(f.category);
          return (
            <div
              key={f.category}
              className="rounded-sm border border-ink/10 bg-cream/40 p-3"
              data-testid={`${testidPrefix}-family-${f.category.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colour }} />
                <p className="overline text-ink truncate" title={f.category}>{f.category}</p>
                <span className="ml-auto text-ink-soft text-[11px] whitespace-nowrap">
                  {f.flavours} flavour{f.flavours === 1 ? "" : "s"}
                </span>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-ink text-lg" data-testid={`${testidPrefix}-family-qty-${f.category.replace(/\s+/g, "-").toLowerCase()}`}>
                  {f.qty} <span className="text-ink-soft text-xs">sold</span>
                </span>
                <span className="text-ink-soft text-sm">·</span>
                <span className="text-ink text-lg" data-testid={`${testidPrefix}-family-revenue-${f.category.replace(/\s+/g, "-").toLowerCase()}`}>
                  RM {money(f.revenue)}
                </span>
              </div>
              {/* Share bar visualises revenue % dominance between families */}
              <div className="mt-2 h-1.5 rounded-full bg-ink/5 overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{ width: `${revShare.toFixed(1)}%`, background: colour }}
                />
              </div>
              <p className="text-[11px] text-ink-soft mt-1">
                {revShare.toFixed(1)}% of revenue · {qtyShare.toFixed(1)}% of units
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="border border-ink/10 rounded-sm bg-cream/40 p-3" data-testid={testid}>
      <p className="overline text-ink-soft">{label}</p>
      <p className="text-ink text-lg mt-1 truncate" title={value}>{value}</p>
      {sub && <p className="text-[11px] text-ink-soft mt-1 truncate">{sub}</p>}
    </div>
  );
}

// Preview panel shown after clicking "Find & group <category>". Every matched
// product is listed with a checkbox — auto-ticked for rows that would move
// (i.e. not already in the target category). Admin can untick any row they
// don't want to migrate, then click "Apply" to bulk-move only the ticked ones.
function ReclassifyPanel({ result, category = "Desserts", testidPrefix = "sales-desserts", onApply, onDismiss, loading }) {
  const matched = result?.matched || [];
  const isDry = !!result?.dry_run;
  // Rows where old_category !== target default to ticked; already-in rows
  // are unticked (nothing to change) but shown for context.
  const [selected, setSelected] = useState(() => {
    const s = {};
    for (const m of matched) {
      if ((m.old_category || "") !== category) s[m.id] = true;
    }
    return s;
  });
  // If the result changed (e.g. re-run of dry-run), re-seed selection.
  useEffect(() => {
    const s = {};
    for (const m of matched) {
      if ((m.old_category || "") !== category) s[m.id] = true;
    }
    setSelected(s);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const migratableCount = matched.filter((m) => (m.old_category || "") !== category).length;
  const allSelected = migratableCount > 0 && selectedCount === migratableCount;

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const shouldSelectAll = !allSelected;
    const s = {};
    for (const m of matched) {
      if ((m.old_category || "") !== category) s[m.id] = shouldSelectAll;
    }
    setSelected(s);
  };

  const doApply = () => {
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    onApply(ids);
  };

  return (
    <div className="rounded-sm border border-matcha-dark/30 bg-sage/10 p-4" data-testid={`${testidPrefix}-reclassify-panel`}>
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <div>
          <p className="overline text-matcha-dark">
            {isDry ? "Preview — tick to select" : "Applied ✓"}
          </p>
          <p className="text-ink text-base">
            {isDry
              ? `${matched.length} product${matched.length === 1 ? "" : "s"} match the ${category.toLowerCase()} allow-list. ${migratableCount} can move (${matched.length - migratableCount} already correct).`
              : `${result?.changed ?? 0} product${(result?.changed ?? 0) === 1 ? "" : "s"} moved into ${category}.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isDry && migratableCount > 0 && (
            <button
              type="button"
              data-testid={`${testidPrefix}-reclassify-apply`}
              disabled={loading || selectedCount === 0}
              onClick={doApply}
              className="px-4 py-2 rounded-sm bg-matcha-dark text-white text-sm hover:bg-matcha disabled:opacity-40 disabled:cursor-not-allowed"
              title={selectedCount === 0 ? "Tick at least one product to migrate." : ""}
            >
              {loading ? "Applying…" : `Apply — move ${selectedCount}`}
            </button>
          )}
          <button
            type="button"
            data-testid={`${testidPrefix}-reclassify-dismiss`}
            onClick={onDismiss}
            className="px-3 py-2 rounded-sm border border-ink/15 text-ink text-sm hover:bg-cream"
          >
            {isDry ? "Cancel" : "Dismiss"}
          </button>
        </div>
      </div>

      {matched.length > 0 && (
        <div className="max-h-64 overflow-y-auto border border-ink/10 rounded-sm bg-bone">
          <table className="w-full text-xs">
            <thead className="text-left text-ink-soft border-b border-ink/10 bg-cream/40 sticky top-0">
              <tr>
                {isDry && (
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      data-testid={`${testidPrefix}-reclass-toggle-all`}
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={migratableCount === 0}
                      title={allSelected ? "Untick all" : "Tick all migratable rows"}
                    />
                  </th>
                )}
                <th className="px-3 py-2 overline">Product</th>
                <th className="px-3 py-2 overline">Was</th>
                <th className="px-3 py-2 overline">Now</th>
              </tr>
            </thead>
            <tbody>
              {matched.map((m) => {
                const alreadyIn = (m.old_category || "") === category;
                const willChange = !alreadyIn && !!selected[m.id];
                return (
                  <tr key={m.id} className="border-t border-ink/5" data-testid={`${testidPrefix}-reclass-row-${m.id}`}>
                    {isDry && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          data-testid={`${testidPrefix}-reclass-tick-${m.id}`}
                          checked={!!selected[m.id]}
                          onChange={() => toggle(m.id)}
                          disabled={alreadyIn}
                          title={alreadyIn ? `Already tagged ${category} — nothing to do` : "Tick to include in the migration"}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2 text-ink">{m.name}</td>
                    <td className="px-3 py-2 text-ink-soft">{m.old_category || "(none)"}</td>
                    <td className={`px-3 py-2 ${willChange ? "text-matcha-dark" : alreadyIn ? "text-ink-soft" : "text-ink-soft/60"}`}>
                      {alreadyIn ? `${category} ✓` : willChange ? `→ ${category}` : "(skipped)"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {matched.length === 0 && (
        <p className="text-ink-soft text-sm py-4 text-center">
          No product names match the {category.toLowerCase()} allow-list.
        </p>
      )}
    </div>
  );
}

const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
